import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';

import {
  IAccount,
  IUpdateDashpivotBillingSettingsRequestDto,
  IUpdateAccountBillingSettingsRequestDto,
  IWorkspace,
  Products,
} from '@site-mate/sitemate-global-shared';

import { JwtService } from 'app/auth/jwt.service';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GlobalApiService {
  constructor(private readonly httpClient: HttpClient, private readonly jwtService: JwtService) {}

  validateSubdomain<T = any>(subdomain: string): Observable<T> {
    const url = `${environment.global.apiUrl}/accounts/validate/subdomain?subdomain=${subdomain}`;

    return this.httpClient.get<T>(url);
  }

  getAccount(accountId: string): Observable<IAccount> {
    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/accounts/${accountId}`;

    return headers$.pipe(switchMap((headers) => this.httpClient.get<IAccount>(url, { headers })));
  }

  updateAccount(accountId: string, account: Partial<IAccount>): Observable<IAccount> {
    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/accounts/${accountId}`;

    return headers$.pipe(switchMap((headers) => this.httpClient.patch<IAccount>(url, account, { headers })));
  }

  getUserInfo(userIds: string[]): Observable<any> {
    const sitemateUserIdsString = userIds.join(',');

    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/user-summary?userIds=${sitemateUserIdsString}`;

    return headers$.pipe(switchMap((headers) => this.httpClient.get<any>(url, { headers })));
  }

  checkWorkspaceMigrationEligibility(fromAccountId: string, toAccountId: string): Observable<boolean> {
    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/workspaces/validate/account-change-eligibility`;

    return headers$.pipe(
      switchMap((headers) =>
        this.httpClient.get<boolean>(url, { headers, params: { fromAccountId, toAccountId } }),
      ),
    );
  }

  updateDashpivotBillingSettings(
    accountId: string,
    settings: IUpdateDashpivotBillingSettingsRequestDto,
  ): Observable<any> {
    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/accounts/${accountId}/billing/dashpivot`;

    return headers$.pipe(switchMap((headers) => this.httpClient.patch(url, settings, { headers })));
  }

  updateAccountBillingSettings(
    accountId: string,
    settings: IUpdateAccountBillingSettingsRequestDto,
  ): Observable<any> {
    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/accounts/${accountId}/billing`;

    return headers$.pipe(switchMap((headers) => this.httpClient.patch(url, settings, { headers })));
  }

  searchWorkspace(searchTerm: string): Observable<IWorkspace[]> {
    const headers$ = from(this.getJwtHeaders());
    const params = new HttpParams({ fromObject: { name: searchTerm, product: Products.Dashpivot } });
    const url = `${environment.global.apiUrl}/workspaces/search`;
    return headers$.pipe(switchMap((headers) => this.httpClient.get<IWorkspace[]>(url, { headers, params })));
  }

  getAccessibleWorkspaces(): Observable<IWorkspace[]> {
    const headers$ = from(this.getJwtHeaders());
    const url = `${environment.global.apiUrl}/workspaces/access`;
    const params = new HttpParams({ fromObject: { product: Products.Dashpivot } });

    return headers$.pipe(switchMap((headers) => this.httpClient.get<IWorkspace[]>(url, { headers, params })));
  }

  private async getJwtHeaders(): Promise<{ Authorization: string; Authtype: string }> {
    const token = await this.jwtService.getToken();

    return {
      Authorization: token.trim(),
      Authtype: 'fusionauth',
    };
  }
}
